import React, { useCallback } from 'react';
import Modal, { ModalTitle, ModalContent, ModalFooter } from '../../../../../../ui/Layout/Modals/Modal/Modal';
import CollectionForm, { CollectionFormValues } from '../components/CollectionForm/CollectionForm';
import { Formik, FormikHelpers } from 'formik';
import { GenericDropdownOption } from '../../../../../../App.types';
import Button from '../../../../../../ui/Buttons/Button/Button';
import { showNotification } from '../../../../../../helpers';
import { TiktokAudioCollection, postCollection, uploadCollectionImage } from '@round/api';
import { mapApiErrorsToFormikErrors } from '../../../../../../utility/utility';

type CreateCollectionModalProps = {
    isModalOpen: boolean;
    closeModal: () => void;
    clientOptions: GenericDropdownOption<number>[];
    onCreated: (collection: TiktokAudioCollection) => void;
};

const initialFormValues: CollectionFormValues = {
    name: '',
    client: null,
    image: [],
};

const CreateCollectionModal = ({ isModalOpen, closeModal, clientOptions, onCreated }: CreateCollectionModalProps) => {
    const handleSubmit = useCallback(
        async (values: CollectionFormValues, { setSubmitting, setErrors }: FormikHelpers<CollectionFormValues>) => {
            if (values.client === null) {
                return;
            }

            try {
                setSubmitting(true);
                const response = await postCollection({
                    name: values.name,
                    client: values.client.value,
                });

                if (response.status === 400) {
                    setErrors(mapApiErrorsToFormikErrors(response.data));
                    return;
                }

                let collection = response.data;
                const image = values.image[0];
                if (image) {
                    const response = await uploadCollectionImage(collection.id, image);
                    if (response.status === 404) {
                        showNotification('Could not upload image. Collection not found.', 'error');
                        return;
                    }

                    collection = response.data;
                }

                onCreated(collection);
                closeModal();
                showNotification('Collection created', 'info');
            } catch {
                showNotification('Could not create collection', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [closeModal, onCreated]
    );

    return (
        <Modal closeOnOverlayClick isOpen={isModalOpen} onClose={closeModal}>
            <ModalTitle>Create collection</ModalTitle>
            <Formik initialValues={initialFormValues} onSubmit={handleSubmit}>
                {({ isSubmitting, submitForm }) => (
                    <>
                        <ModalContent>
                            <CollectionForm clientOptions={clientOptions} />
                        </ModalContent>
                        <ModalFooter>
                            <Button type="filled" color="black" loading={isSubmitting} onClick={submitForm}>
                                Create
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </Formik>
        </Modal>
    );
};

export default CreateCollectionModal;
